import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import PlatformFooter from '../components/PlatformFooter';
import { Button, Field, Input, Textarea } from '../components/ui';
import { contactService } from '../services/api';

const EMPTY_FORM = { name: '', email: '', phone: '', subject: '', message: '', website: '' };

function ContactUs() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined, general: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      await contactService.sendMessage(form);
      setForm(EMPTY_FORM);
      setSent(true);
    } catch (error) {
      const data = error.response?.data;
      setErrors(data && typeof data === 'object' ? data : { general: t('Unable to send your message. Please try again.') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 text-gray-900">
      <header className="border-b border-surface-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/"><Logo size={34} textColor="text-gray-900" /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login" className="text-sm font-semibold text-primary-700 hover:text-primary-900">{t('Login')}</Link>
          </div>
        </div>
      </header>

      <main className="relative overflow-hidden px-6 py-12 sm:py-20">
        <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-primary-200/35 blur-3xl" aria-hidden="true" />
        <div className="absolute -right-28 bottom-12 h-72 w-72 rounded-full bg-accent-200/30 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <section className="pt-4">
            <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-700">
              {t('Contact us')}
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold tracking-tight text-gray-950 sm:text-5xl">
              {t('How can we help?')}
            </h1>
            <p className="mt-5 max-w-md text-lg leading-8 text-gray-600">
              {t('Ask us about courses, enrollment, partnerships, or anything else. Our team will review your message and get back to you.')}
            </p>
            <div className="mt-8 rounded-2xl border border-primary-100 bg-primary-50/80 p-5">
              <div className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-16 9h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{t('Your message goes directly to the administration')}</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600">{t('You do not need an account to contact us.')}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-white bg-white p-6 shadow-card sm:p-8">
            {sent ? (
              <div className="flex min-h-[430px] flex-col items-center justify-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </span>
                <h2 className="mt-5 text-2xl font-bold">{t('Message sent successfully')}</h2>
                <p className="mt-2 max-w-sm text-gray-600">{t('Thank you for contacting us. The administration has received your message.')}</p>
                <Button className="mt-7" onClick={() => setSent(false)}>{t('Send another message')}</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                {errors.general && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errors.general}</div>}
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t('Full name')} required error={errors.name?.[0]}>
                    <Input value={form.name} onChange={update('name')} maxLength={150} autoComplete="name" required />
                  </Field>
                  <Field label={t('Email')} required error={errors.email?.[0]}>
                    <Input type="email" value={form.email} onChange={update('email')} maxLength={254} autoComplete="email" required />
                  </Field>
                </div>
                <Field label={t('Phone number (optional)')} error={errors.phone?.[0]}>
                  <Input type="tel" value={form.phone} onChange={update('phone')} maxLength={30} autoComplete="tel" />
                </Field>
                <Field label={t('Subject')} required error={errors.subject?.[0]}>
                  <Input value={form.subject} onChange={update('subject')} maxLength={200} required />
                </Field>
                <Field label={t('Message')} required error={errors.message?.[0]} hint={t('Please include enough detail so we can help you quickly.')}>
                  <Textarea value={form.message} onChange={update('message')} rows={6} maxLength={5000} required />
                </Field>
                <div className="hidden" aria-hidden="true">
                  <label>Website<input tabIndex="-1" autoComplete="off" value={form.website} onChange={update('website')} /></label>
                </div>
                <Button type="submit" size="lg" loading={submitting} className="w-full">
                  {submitting ? t('Sending...') : t('Send message')}
                </Button>
              </form>
            )}
          </section>
        </div>
      </main>
      <PlatformFooter />
    </div>
  );
}

export default ContactUs;

